import { useMemo } from 'react';

import { useProjects } from 'src/graphql/entities/project';
import { ConditionInput, SortingInput } from 'src/graphql/types';

import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const DEFAULT_FILTER: ConditionInput[] = [];

const DEFAULT_TARGET_IDS = [
  'project/projectId',
  'address/projects/projectId',
  'clientEmail/client/addresses/projects/projectId',
  'clientPhoneNumber/client/addresses/projects/projectId',
];

export const useSearchProjects = (params: {
  searchTerm: string | null;
  targetIdAttributes?: string[];
  filter?: ConditionInput[];
  limit?: number;
  sort?: SortingInput | SortingInput[];
  skip?: boolean;
  showResultsWhenEmptySearchTerm?: boolean;
  partnerId?: number | null;
  dynamicAttributePaths?: string[];
}) => {
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const {
    filter = DEFAULT_FILTER,
    skip,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    limit,
    partnerId,
    dynamicAttributePaths,
  } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: targetIdAttributes ?? DEFAULT_TARGET_IDS,
        }
      : undefined;

    return {
      filters: filter,
      sort,
      limit,
      offset: 0,
      searchParams,
      partnerId,
      dynamicAttributePaths,
    };
  }, [targetIdAttributes, filter, limit, searchTerm, sort, partnerId, dynamicAttributePaths]);

  const { projects, loading, ...rest } = useProjects(queryOptions, {
    skip: skip || (!showResultsWhenEmptySearchTerm && !searchTerm),
  });

  return {
    ...rest,
    isLoading: loading,
    records: projects,
  };
};
